import React, { useEffect, useRef } from 'react'
import CSS from 'csstype'

import {
  useInspectorContext,
  useMapPRMarinaRectToCanvasRelativeRect,
} from '../../../hooks/inspector'
import {
  ElementType,
  SketchElement,
  isSectionElement,
  isTopLevelFrameElement,
  isVariantSetElement,
} from '../../../../../inspector'
import {
  EntityOverlay,
  EntityOverlayProps,
  TouchableClickEvent,
} from '../../../../../canvas/components'
import { OVERLAYS_Z_INDEX } from '../constants'

import { LayerOverlay as LayerOverlayStyledComponent } from '../FrameDetailInspectorOverlay.styles'
import * as S from './SelectedElementOverlays.styles'
import { Rect } from '@sketch-hq/sketch-web-renderer'
import { useStableHandler } from '@sketch/utils'

import {
  useNavigateToFrame,
  useNavigateToSection,
} from '../../../../../canvas/hooks'

// The corner SVGs are drawn using an outer border which means they are drawn at a 1
// pixel offset.
const CORNER_OFFSET = 1
const SELECTED_LAYER_MIN_SIZE_TO_SHOW_CORNERS = 12

type SelectedElementOverlaysProps = {
  selectedElement: SketchElement | null
  showFrameOverlay: boolean
}
export function SelectedElementOverlays({
  selectedElement,
  showFrameOverlay,
}: SelectedElementOverlaysProps) {
  const { sketchSceneRootElement } = useInspectorContext()

  // We don't show artboard as selected element.
  if (!selectedElement || !sketchSceneRootElement) {
    return null
  }

  return (
    <SelectionRectangle
      selectedElement={selectedElement}
      showFrameOverlay={showFrameOverlay}
    />
  )
}

type SelectionRectangleProps = {
  selectedElement: SketchElement
  showFrameOverlay: boolean
}
function SelectionRectangle({
  selectedElement,
  showFrameOverlay,
}: SelectionRectangleProps) {
  const bounds = selectedElement.prMarinaNode.getAbsoluteLayerBounds()
  const canvasRelativeRect = useMapPRMarinaRectToCanvasRelativeRect(bounds)

  if (!canvasRelativeRect) {
    return null
  }

  const { x, y, width, height } = canvasRelativeRect
  // Don't show the corners if the selected rectangle is very small
  // as the corner won't fit.
  const showMinimalVersion =
    width < SELECTED_LAYER_MIN_SIZE_TO_SHOW_CORNERS ||
    height < SELECTED_LAYER_MIN_SIZE_TO_SHOW_CORNERS

  const variantProperties =
    selectedElement.type === ElementType.SymbolMaster &&
    selectedElement.variantConfiguration.length > 0
      ? selectedElement.variantConfiguration.map(
          ({ propertyName, valueName }) => ({ propertyName, valueName })
        )
      : undefined

  const artboardOverlay = showFrameOverlay &&
    (isTopLevelFrameElement(selectedElement) ||
      isSectionElement(selectedElement)) && (
      <ArtboardSelectionRectangle
        artboardName={selectedElement.name}
        artboardUUID={selectedElement.elementUUID}
        bounds={canvasRelativeRect}
        isSymbol={selectedElement.type === ElementType.SymbolMaster}
        variantProperties={variantProperties}
        isSection={isSectionElement(selectedElement)}
        isVariantSet={isVariantSetElement(selectedElement)}
      />
    )

  if (showMinimalVersion) {
    return (
      <>
        {artboardOverlay}
        <LayerOverlayStyledComponent
          data-testid="selected-layer-overlay"
          $borderSize={1}
          $color="orange"
          $width={width}
          $height={height}
          $x={x}
          $y={y}
          $zIndex={OVERLAYS_Z_INDEX.SELECTION_RECTANGLE}
        />
      </>
    )
  }

  const cornerStyles: CSS.Properties = {
    position: 'absolute',
    width: `6px`,
    height: `6px`,
  }

  return (
    <>
      {artboardOverlay}
      <S.SelectionRectangleContainer
        $width={width}
        $height={height}
        $x={x}
        $y={y}
        data-testid="selected-layer-overlay"
      >
        <S.SelectionRectangleTopEdgeLine />
        <S.SelectionRectangleBottomEdgeLine />
        <S.SelectionRectangleLeftEdgeLine />
        <S.SelectionRectangleRightEdgeLine />

        <S.TopLeftCornerIcon
          style={{
            top: -CORNER_OFFSET,
            left: -CORNER_OFFSET,
            ...cornerStyles,
          }}
        />
        <S.TopRightCornerIcon
          style={{
            top: -CORNER_OFFSET,
            right: -CORNER_OFFSET,
            ...cornerStyles,
          }}
        />
        <S.BottomLeftCornerIcon
          style={{
            bottom: -CORNER_OFFSET,
            left: -CORNER_OFFSET,
            ...cornerStyles,
          }}
        />
        <S.BottomRightCornerIcon
          style={{
            bottom: -CORNER_OFFSET,
            right: -CORNER_OFFSET,
            ...cornerStyles,
          }}
        />
      </S.SelectionRectangleContainer>
    </>
  )
}

interface ArtboardSelectionRectangleProps {
  artboardUUID: string
  artboardName: string
  bounds: Rect
  isSymbol?: boolean
  variantProperties?: EntityOverlayProps['variantProperties']
  isSection?: boolean
  isVariantSet?: boolean
}

function ArtboardSelectionRectangle(props: ArtboardSelectionRectangleProps) {
  const {
    artboardUUID,
    artboardName,
    bounds,
    isSymbol,
    isSection,
    isVariantSet,
    variantProperties,
  } = props
  const artboardRef = useRef<HTMLDivElement>(null)

  const navigateToFrame = useNavigateToFrame()
  const navigateToSection = useNavigateToSection()
  const navigateToEntity = isSection ? navigateToSection : navigateToFrame
  const { setSelectedElement } = useInspectorContext()

  const handleWindowKeyPress = useStableHandler((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setSelectedElement(null)
      return
    }

    if (event.key !== 'Enter') return

    // Only one selection overlay is mounted at a time (for the selected
    // entity), so Enter here always targets it. We can't gate on the overlay
    // holding DOM focus - its container has no tab index (removed so the canvas
    // doesn't translate on Tab), so it can never become the active element.
    // Instead, skip when focus is on a control that gives Enter its own meaning
    // (text entry, buttons, links, menu items) so we don't hijack it.
    const activeElement = global.document.activeElement
    const isInteractiveTarget =
      activeElement instanceof HTMLElement &&
      (activeElement.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].includes(
          activeElement.tagName
        ) ||
        activeElement.closest(
          '[role="button"], [role="link"], [role="menuitem"]'
        ) !== null)

    if (isInteractiveTarget) return

    navigateToEntity(artboardUUID, 'keyPressEnter')
  })

  useEffect(() => {
    window.document.body.addEventListener('keydown', handleWindowKeyPress)

    return () => {
      window.document.body.removeEventListener('keydown', handleWindowKeyPress)
    }
  }, [handleWindowKeyPress])

  const onCTAClick = useStableHandler(
    (e: TouchableClickEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      navigateToEntity(artboardUUID, 'CTA')
    }
  )

  return (
    <EntityOverlay
      artboardName={artboardName}
      artboardUUID={artboardUUID}
      bounds={bounds}
      onCtaClick={onCTAClick}
      ref={artboardRef}
      isComponent={isSymbol}
      isVariantSet={isVariantSet}
      variantProperties={variantProperties}
      selected
    />
  )
}
